import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicNav, Footer } from "@/components/PublicChrome";
import { Embers } from "@/components/Embers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Link2, DollarSign, TrendingUp, Users, Copy, Check, Wallet, Send,
  Sparkles, ArrowRight, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MIN_WITHDRAWAL = 50;

const StatCard = ({ icon: Icon, label, value, sub, testid, accent = false }) => (
  <div className={`glass rounded-xl p-5 ${accent ? "border-[#ff7a00]/40" : ""}`} data-testid={testid}>
    <div className="w-10 h-10 rounded-lg bg-[#ff7a00]/10 border border-[#ff7a00]/30 flex items-center justify-center text-[#ff7a00]">
      <Icon size={18} />
    </div>
    <div className="mt-4 font-display text-2xl font-bold text-[#f5f5f5]">{value}</div>
    <div className="text-xs text-[#8a8a8a] mt-1 uppercase tracking-wider">{label}</div>
    {sub && <div className="text-xs text-[#ff9a2f] mt-1">{sub}</div>}
  </div>
);

const AuthedAffiliate = () => {
  const [profile, setProfile] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [copied, setCopied] = useState(false);
  const [wdOpen, setWdOpen] = useState(false);
  const [wdForm, setWdForm] = useState({ amount: "", wallet_address: "" });
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([
    api.get("/affiliate/me").then((r) => setProfile(r.data)),
    api.get("/affiliate/commissions").then((r) => setCommissions(r.data || [])),
    api.get("/affiliate/withdrawals").then((r) => setWithdrawals(r.data || [])),
  ]);
  useEffect(() => { load().catch(() => {}); }, []);

  if (!profile) {
    return <div className="text-[#8a8a8a] py-20 text-center" data-testid="affiliate-loading">Loading your affiliate dashboard…</div>;
  }

  const copyLink = () => {
    navigator.clipboard.writeText(profile.referral_link);
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const submitWithdraw = async (e) => {
    e.preventDefault();
    const amt = parseFloat(wdForm.amount);
    if (isNaN(amt) || amt < MIN_WITHDRAWAL) {
      toast.error(`Minimum withdrawal is $${MIN_WITHDRAWAL}`);
      return;
    }
    if (amt > profile.available_balance) {
      toast.error(`Only $${profile.available_balance.toFixed(2)} available`);
      return;
    }
    setBusy(true);
    try {
      await api.post("/affiliate/withdraw", { amount: amt, wallet_address: wdForm.wallet_address.trim() });
      toast.success("Withdrawal request submitted");
      setWdOpen(false);
      setWdForm({ amount: "", wallet_address: "" });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#f5f5f5]">
      <PublicNav />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#ff7a00]/10 py-14 lg:py-20">
        <Embers count={18} />
        <div className="absolute -top-40 -left-32 w-[500px] h-[500px] bg-[#ff7a00]/15 rounded-full blur-[160px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-[#ff7a00] text-xs tracking-[0.3em] uppercase mb-3">Affiliate Program</div>
          <motion.h1
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="font-display text-4xl sm:text-5xl font-bold leading-tight"
          >
            Share Funded Index. <span className="text-[#ff7a00] text-glow-orange">Earn 10%.</span>
          </motion.h1>
          <p className="text-[#8a8a8a] mt-3 max-w-2xl">
            Earn <strong className="text-[#ff9a2f]">10% commission</strong> on every challenge purchased through your link. Paid in USDT (BEP20). Minimum withdrawal ${MIN_WITHDRAWAL}.
          </p>

          {/* Referral link */}
          <div className="mt-8 max-w-3xl">
            <Label className="text-[10px] uppercase tracking-widest text-[#cfcfcf]">Your Referral Link</Label>
            <div className="mt-2 flex flex-col sm:flex-row gap-3">
              <div className="flex-1 glass rounded-lg px-4 py-3 font-mono text-sm text-[#f5f5f5] truncate" data-testid="affiliate-referral-link">
                {profile.referral_link}
              </div>
              <Button
                onClick={copyLink}
                className="bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold h-12 px-6"
                data-testid="affiliate-copy-link"
              >
                {copied ? <><Check size={16} className="mr-2" />Copied</> : <><Copy size={16} className="mr-2" />Copy Link</>}
              </Button>
            </div>
            <div className="mt-2 text-xs text-[#8a8a8a]">
              Code: <span className="text-[#ff9a2f] font-mono font-semibold" data-testid="affiliate-code">{profile.referral_code}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard icon={DollarSign} label="Total Earned" value={`$${profile.total_earned.toFixed(2)}`} accent testid="stat-total-earned" />
          <StatCard icon={Wallet} label="Available" value={`$${profile.available_balance.toFixed(2)}`} testid="stat-available" />
          <StatCard icon={TrendingUp} label="Pending Payout" value={`$${profile.pending_withdrawal.toFixed(2)}`} testid="stat-pending" />
          <StatCard icon={Check} label="Paid Out" value={`$${profile.paid_out.toFixed(2)}`} testid="stat-paid-out" />
          <StatCard icon={Users} label="Referrals" value={profile.total_referrals} testid="stat-referrals" />
        </div>
      </section>

      {/* Withdraw CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="glass rounded-xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div>
            <div className="text-[10px] text-[#8a8a8a] uppercase tracking-widest">Withdraw Earnings</div>
            <div className="font-display text-xl sm:text-2xl font-bold mt-1">Cash out to USDT (BEP20)</div>
            <p className="text-[#8a8a8a] text-sm mt-1">Minimum ${MIN_WITHDRAWAL}. Requests are reviewed by our team within 24–48 hours.</p>
          </div>
          <Dialog open={wdOpen} onOpenChange={setWdOpen}>
            <DialogTrigger asChild>
              <Button
                disabled={profile.available_balance < MIN_WITHDRAWAL}
                className="bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold h-12 px-6 disabled:bg-[#1a1a1a] disabled:text-[#8a8a8a]"
                data-testid="affiliate-withdraw-button"
              >
                <Send size={16} className="mr-2" />
                {profile.available_balance < MIN_WITHDRAWAL ? `$${MIN_WITHDRAWAL} minimum` : "Request Withdrawal"}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-[#ff7a00]/30 text-[#f5f5f5]" data-testid="affiliate-withdraw-dialog">
              <DialogHeader>
                <DialogTitle className="font-display text-xl">Request Withdrawal</DialogTitle>
              </DialogHeader>
              <form onSubmit={submitWithdraw} className="space-y-4">
                <div className="glass rounded-lg p-4">
                  <div className="text-[10px] text-[#8a8a8a] uppercase tracking-widest">Available Balance</div>
                  <div className="font-display text-2xl font-bold text-[#ff7a00] mt-1">${profile.available_balance.toFixed(2)}</div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">Amount (USD)</Label>
                  <Input
                    type="number" min={MIN_WITHDRAWAL} max={profile.available_balance} step="0.01" required
                    value={wdForm.amount}
                    onChange={(e) => setWdForm({ ...wdForm, amount: e.target.value })}
                    className="bg-[#050505] border-[#ff7a00]/20 mt-2 h-11"
                    placeholder={`${MIN_WITHDRAWAL}.00`}
                    data-testid="affiliate-withdraw-amount"
                  />
                  <div className="text-[10px] text-[#8a8a8a] mt-1">Minimum ${MIN_WITHDRAWAL}</div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#cfcfcf]">USDT (BEP20) Wallet Address</Label>
                  <Input
                    required minLength={10}
                    value={wdForm.wallet_address}
                    onChange={(e) => setWdForm({ ...wdForm, wallet_address: e.target.value })}
                    className="bg-[#050505] border-[#ff7a00]/20 mt-2 h-11 font-mono text-sm"
                    placeholder="0x..."
                    data-testid="affiliate-withdraw-wallet"
                  />
                  <div className="text-[10px] text-[#8a8a8a] mt-1 flex items-center gap-1.5">
                    <ShieldCheck size={11} className="text-[#ff9a2f]" /> Payments only made on BSC (BNB Smart Chain). Double-check your address.
                  </div>
                </div>
                <Button type="submit" disabled={busy} className="w-full h-12 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold" data-testid="affiliate-withdraw-submit">
                  {busy ? "Submitting…" : "Submit Request"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* Commissions + Withdrawals tables */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 grid lg:grid-cols-2 gap-5">
        <div className="glass rounded-xl overflow-hidden" data-testid="commissions-table">
          <div className="p-5 border-b border-[#ff7a00]/15 flex items-center justify-between">
            <div className="font-display text-lg font-semibold">Recent Commissions</div>
            <Badge className="bg-[#ff7a00]/15 text-[#ff9a2f] border-[#ff7a00]/30 text-[10px] uppercase tracking-widest">10% rate</Badge>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] tracking-widest text-[#8a8a8a] uppercase border-b border-[#ff7a00]/10">
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Referral</th>
                  <th className="text-left p-3">Challenge</th>
                  <th className="text-right p-3">Commission</th>
                </tr>
              </thead>
              <tbody>
                {commissions.length === 0 && (
                  <tr><td colSpan={4} className="p-8 text-center text-[#8a8a8a] text-sm">
                    <Sparkles className="mx-auto text-[#ff7a00] mb-2" size={20} />
                    Share your link — commissions land here.
                  </td></tr>
                )}
                {commissions.map((c) => (
                  <tr key={c.id} className="border-b border-[#ff7a00]/5" data-testid={`commission-${c.id}`}>
                    <td className="p-3 text-[#8a8a8a]">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="p-3 text-[#cfcfcf] text-xs">{c.referred_user_email || c.referred_user_id.slice(0, 8)}</td>
                    <td className="p-3 text-[#f5f5f5] text-xs">{c.challenge_name || "—"}</td>
                    <td className="p-3 text-right font-display font-bold text-[#ff7a00]">+${c.commission_usd.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="glass rounded-xl overflow-hidden" data-testid="withdrawals-table">
          <div className="p-5 border-b border-[#ff7a00]/15">
            <div className="font-display text-lg font-semibold">Withdrawal History</div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] tracking-widest text-[#8a8a8a] uppercase border-b border-[#ff7a00]/10">
                  <th className="text-left p-3">Date</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-left p-3">Wallet</th>
                  <th className="text-left p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.length === 0 && (
                  <tr><td colSpan={4} className="p-8 text-center text-[#8a8a8a] text-sm">No withdrawals yet.</td></tr>
                )}
                {withdrawals.map((w) => (
                  <tr key={w.id} className="border-b border-[#ff7a00]/5" data-testid={`withdrawal-${w.id}`}>
                    <td className="p-3 text-[#8a8a8a]">{new Date(w.created_at).toLocaleDateString()}</td>
                    <td className="p-3 text-right font-display font-bold text-[#ff7a00]">${w.amount.toFixed(2)}</td>
                    <td className="p-3 font-mono text-[10px] text-[#8a8a8a] max-w-[120px] truncate">{w.wallet_address}</td>
                    <td className="p-3">
                      <Badge className={`uppercase text-[10px] tracking-widest ${
                        w.status === "paid" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
                        w.status === "approved" ? "bg-[#ff7a00]/15 text-[#ff7a00] border-[#ff7a00]/30" :
                        w.status === "rejected" ? "bg-red-500/15 text-red-400 border-red-500/30" :
                        "bg-yellow-500/15 text-yellow-400 border-yellow-500/30"
                      }`}>{w.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Public "join the program" view for logged-out visitors
const PublicAffiliate = () => (
  <div className="min-h-screen bg-[#050505] text-[#f5f5f5]">
    <PublicNav />
    <section className="relative overflow-hidden border-b border-[#ff7a00]/10 py-20 lg:py-28">
      <Embers count={22} />
      <div className="absolute -top-40 -left-32 w-[520px] h-[520px] bg-[#ff7a00]/15 rounded-full blur-[160px]" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#ff7a00]/40 bg-[#ff7a00]/5 text-[#ff9a2f] text-xs tracking-[0.3em] uppercase"
          data-testid="affiliate-public-badge">
          <span className="w-1.5 h-1.5 rounded-full bg-[#ff7a00] animate-pulse" /> Now Live — 10% commission
        </motion.div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mt-6">
          Refer the pack. <br className="hidden sm:block" />
          <span className="text-[#ff7a00] text-glow-orange">Earn 10%.</span>
        </h1>
        <p className="text-[#cfcfcf] text-base sm:text-lg mt-6 max-w-2xl mx-auto">
          Every trader who buys a challenge through your link earns you a 10% commission — paid in USDT (BEP20) with a $50 minimum withdrawal.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/signup" data-testid="affiliate-cta-signup"><Button className="h-12 px-6 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold">Sign up to earn <ArrowRight size={16} className="ml-2" /></Button></Link>
          <Link to="/login" data-testid="affiliate-cta-login"><Button variant="outline" className="h-12 px-6 border-[#ff7a00]/30 text-[#cfcfcf] hover:bg-[#ff7a00]/10 hover:text-[#ff9a2f]">Log in</Button></Link>
        </div>
      </div>
    </section>

    <section className="py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { icon: DollarSign, title: "10% Commission", body: "Earn on every challenge purchased through your link — lifetime." },
          { icon: Link2, title: "Personal Link", body: "Unique referral URL + code. Track every click and conversion." },
          { icon: Wallet, title: "USDT (BEP20)", body: "Cash out in stablecoin on BNB Smart Chain. Fast, cheap fees." },
          { icon: Send, title: "$50 Minimum", body: "Request withdrawals whenever you cross the $50 threshold." },
        ].map((p, i) => (
          <div key={p.title} className="glass rounded-xl p-6" data-testid={`affiliate-perk-${i}`}>
            <div className="w-11 h-11 rounded-lg bg-[#ff7a00]/10 border border-[#ff7a00]/30 flex items-center justify-center text-[#ff7a00] mb-4">
              <p.icon size={20} />
            </div>
            <div className="font-display text-lg font-semibold">{p.title}</div>
            <div className="text-[#8a8a8a] text-sm mt-2">{p.body}</div>
          </div>
        ))}
      </div>
    </section>
    <Footer />
  </div>
);

export default function AffiliatePage() {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#ff7a00]" data-testid="affiliate-loading-full">Loading…</div>;
  }
  return user ? <AuthedAffiliate /> : <PublicAffiliate />;
}
